import React from 'react';
import { motion } from 'framer-motion';
import { Award, ArrowRight, Star, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import mentorImage from '../assets/sir.jfif';

const MentorSection = () => {
  return (
    <section className="py-8 sm:py-10 relative overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        <div className="absolute top-0 right-0 w-48 sm:w-72 h-48 sm:h-72 bg-primary-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-48 sm:w-72 h-48 sm:h-72 bg-accent-500/10 rounded-full blur-3xl"></div>
      </div>

      <div className="container-max relative px-4 sm:px-6">
        <div className="text-center mb-6 sm:mb-8">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="inline-block bg-primary-500/10 backdrop-blur-sm px-3 sm:px-4 py-1.5 rounded-full"
          >
            <span className="text-xs sm:text-sm font-medium text-primary-400">Meet Our Mentor</span>
          </motion.div>
        </div>
        
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="grid grid-cols-1 lg:grid-cols-5 gap-6 sm:gap-8 items-center"
        >
          {/* Left Column - Square Image */}
          <div className="relative flex justify-center lg:col-span-2 order-1">
            <motion.div
              initial={{ scale: 0.95 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative flex items-center justify-center"
              style={{ minHeight: '280px', maxHeight: '520px' }}
            >
              {/* Decorative glows */}
              <div className="absolute -top-6 sm:-top-10 -left-6 sm:-left-10 w-32 sm:w-56 h-32 sm:h-56 bg-primary-500/20 rounded-lg blur-2xl z-0"></div>
              <div className="absolute -bottom-6 sm:-bottom-10 -right-6 sm:-right-10 w-40 sm:w-72 h-40 sm:h-72 bg-blue-500/20 rounded-lg blur-2xl z-0"></div>
              {/* Square border frame */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                <div className="w-[280px] h-[350px] sm:w-[320px] sm:h-[400px] lg:w-[420px] lg:h-[520px] rounded-lg border-2 sm:border-4 border-white/10"></div>
              </div>
              {/* Main Square Image */}
              <div className="relative w-[280px] h-[350px] sm:w-[320px] sm:h-[400px] lg:w-[420px] lg:h-[520px] rounded-lg overflow-hidden shadow-2xl group z-1">
                <img
                  src={mentorImage}
                  alt="Dr. Dharmendra Bajpai"
                  className="w-full h-full object-cover object-center rounded-lg transition-transform duration-700 group-hover:scale-105"
                  style={{
                    filter: 'contrast(1.05) brightness(1.05)',
                    boxShadow: 'inset 0 0 30px rgba(0,0,0,0.5)'
                  }}
                />
                {/* Enhanced gradient overlay */}
                <div className="absolute inset-0 rounded-lg bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-80 group-hover:opacity-70 transition-opacity duration-700"></div>
                {/* Subtle highlight effect */}
                <div className="absolute inset-0 rounded-lg bg-gradient-to-tr from-primary-500/10 to-transparent opacity-80"></div>
                {/* Corner accent (square, so subtle ring) */}
                <div className="absolute inset-0 rounded-lg border-2 border-primary-400/30 pointer-events-none"></div>
              </div>
            </motion.div>
          </div>

          {/* Right Column - Content */}
          <div className="space-y-4 sm:space-y-6 lg:col-span-3 order-2">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-3 sm:space-y-4"
            >
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-1">
                <div className="bg-primary-500/20 backdrop-blur-sm p-2 rounded-full self-start sm:self-auto">
                  <Award className="w-5 h-5 sm:w-6 sm:h-6 text-primary-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl sm:text-2xl font-bold text-white mb-1 sm:mb-0">Dr. Dharmendra Bajpai</h3>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-0">
                    <p className="text-sm text-white/90 sm:mr-3">Founder & Chief Mentor</p>
                    {/* <div className="flex items-center">
                      {[1, 2, 3, 4, 5].map((_, index) => (
                        <Star key={index} className="w-3 h-3 sm:w-3.5 sm:h-3.5 fill-current text-yellow-400" />
                      ))}
                      <span className="ml-1.5 text-xs text-white/80">Industry Leading Expert</span>
                    </div> */}
                  </div>
                </div>
              </div>

              <p className="text-sm sm:text-base text-white/90 leading-relaxed">
                With over <span className="text-primary-400 font-semibold">26+ years</span> of 
                 excellence, Dr. Dharmendra Bajpai has transformed the lives of more than 
                <span className="text-primary-400 font-semibold"> 15,000 students</span> through 
                physical classroom training and mentorship.
              </p>

              {/* Achievements List - Matching the design */}
              <div className="grid grid-cols-1 gap-y-2 sm:gap-y-3 pt-2">
                {[
                  'Trained 15,000+ IT Professionals in physical Classroom',
                  'Programming Expert with 26+ Years Experience',
                  'Specialized in Solid Programming Foundation & DSA',
                  'Delivering Zero Error Programming since inception',
                  'Conducted Off Campus Recruitement Drives for Freshers'
                ].map((achievement, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.1 * index }}
                    className="flex items-start gap-3 group"
                  >
                    <div className="bg-primary-500/10 p-1 rounded-full group-hover:bg-primary-500/20 transition-colors duration-200 flex-shrink-0 mt-0.5">
                      <CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary-400" />
                    </div>
                    <span className="text-xs sm:text-sm text-white/80 group-hover:text-white transition-colors duration-200 leading-relaxed">{achievement}</span>
                  </motion.div>
                ))}
              </div>

              {/* CTA Buttons - Matching the design */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4">
                <Link
                  to="/enquire"
                  className="inline-flex items-center justify-center px-4 sm:px-6 py-2.5 sm:py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-lg font-medium transition-all duration-200 group text-sm sm:text-base"
                >
                  <span>Book Free Consultation</span>
                  <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/why-osop"
                  className="inline-flex items-center justify-center px-4 sm:px-6 py-2.5 sm:py-3 border border-white/20 hover:bg-white/10 text-white rounded-lg font-medium transition-all duration-200 group text-sm sm:text-base"
                >
                  <span>Learn More</span>
                  <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default MentorSection; 